/**
 * Accepting and skipping an offer.
 *
 * One implementation for every rider surface that offers an accept button. The
 * dash home and the offers page each had their own copy; they had drifted in two
 * ways that mattered, and both are fixed here rather than at the call sites:
 *
 *  - Only the offers page cleared `logistics.offer_candidates`, so an offer
 *    accepted from the dash home reappeared in the ranked list.
 *  - The dash home navigated to the active-delivery page after accepting, which
 *    is what made the two paths feel like different features.
 *
 * Neither surface navigates now. Accepting promotes the offer to a delivery, so
 * the Jobs screen renders the active delivery on the next read — the screen
 * changes because the rider's work changed, not because a handler said so.
 */
import { useCallback, useState } from 'react'
import { commands, isLiveRuntime } from '../../lib/runtime'
import { getStore, text, useRiderId } from './records'

export type OfferNotice = '' | 'error' | 'declined'

export interface OfferActions {
  /** Offer id currently being accepted or skipped, or `''` when idle. */
  busyId: string
  notice: OfferNotice
  accept: (record: Record<string, unknown>) => Promise<void>
  decline: (record: Record<string, unknown>) => Promise<void>
}

/** Every store an accepted or skipped offer has to leave. */
const OFFER_STORES = ['offer.offers', 'logistics.offer_candidates'] as const

export const useOfferActions = (): OfferActions => {
  const riderId = useRiderId()
  const [busyId, setBusyId] = useState('')
  const [notice, setNotice] = useState<OfferNotice>('')

  const clear = useCallback((offerId: string) => {
    for (const key of OFFER_STORES) getStore(key)?.remove(offerId)
  }, [])

  const accept = useCallback(
    async (record: Record<string, unknown>) => {
      const offerId = text(record, ['id', 'offer_id'])
      if (!offerId || busyId) return
      setBusyId(offerId)
      setNotice('')
      try {
        if (isLiveRuntime()) {
          await commands.offer.accept(offerId, riderId)
        } else {
          // The command still runs so its envelope and correlation id are the
          // ones the live path uses; the offline lane must not block on it.
          void commands.offer.accept(offerId, riderId).catch(() => undefined)
          const deliveryId = text(record, ['delivery_id'], `delivery-${offerId}`)
          getStore('delivery.deliveries')?.upsert({
            ...record,
            id: deliveryId,
            delivery_id: deliveryId,
            rider_id: riderId,
            stage: 'pickup',
            merchant_display_name: text(record, ['restaurant_name', 'merchant_name'], 'Pickup'),
          })
          clear(offerId)
        }
      } catch {
        setNotice('error')
      } finally {
        setBusyId('')
      }
    },
    [busyId, clear, riderId],
  )

  const decline = useCallback(
    async (record: Record<string, unknown>) => {
      const offerId = text(record, ['id', 'offer_id'])
      if (!offerId || busyId) return
      setBusyId(offerId)
      setNotice('')
      try {
        if (isLiveRuntime()) await commands.offer.decline(offerId, riderId)
        else {
          void commands.offer.decline(offerId, riderId).catch(() => undefined)
          clear(offerId)
        }
        setNotice('declined')
      } catch {
        setNotice('error')
      } finally {
        setBusyId('')
      }
    },
    [busyId, clear, riderId],
  )

  return { busyId, notice, accept, decline }
}
